/* eslint-disable indent */
import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { FormField } from '@/components/Form/FormField';
import { FormEvent, useEffect, useState } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { FormSectionTitle } from '@/components/Form/FormSectionTitle';
import { useListAllEquipments } from '@/hooks/equipment.hooks';
import { Checkbox } from '@/components/Controls/Checkbox';
import { allFieldErrorsEmpty } from '@/utils/object.utils';
import { ErrorMessage } from '@/components/Message/ErrorMessage';
import { getTranslatedItem } from '@/utils/language.utils';
import { setCheckPointRequiredFields } from '@/utils/required-fields/checkPoint.utils';
import { Switch } from '@/components/Controls/Switch';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { twMerge } from 'tailwind-merge';
import { NOT_AUTOMATIC_VALIDATION_CHECK_POINT_TIMEOUT_DEFAULT_VALUE } from '@/schemas/check-point/check-point.schema';
import { Accordion } from '@/components/Accordion';
import { useRowDragAnDrop } from '@/hooks/drag-and-drop.hooks';
import { getSortedList } from '@/hooks/list-order';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { Loader } from '@/components/Loading/Loader';
// import { useUpdateCheckPoint } from '@/hooks/check-point.hooks';

enum EquipmentTab {
  IDENTIFIABLE = 'IDENTIFIABLE',
  REQUIRED = 'REQUIRED',
  OPTIONAL = 'OPTIONAL',
}

interface IOppositeField {
  ids: string[];
  name: string;
}
interface IOnCheckEquipment {
  id: string;
  currentIds: string[];
  currentFieldName: string;
  oppositeFields: IOppositeField[];
  linkFields?: IOppositeField[];
}

export const CheckPointConfigsManagement = <TSchema extends FieldValues>({
  t,
  data,
  control,
  register,
  setValue,
  watch,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const [activeTab, setActiveTab] = useState(EquipmentTab.IDENTIFIABLE);
  const [nbDetectedEquipments, setNbDetectedEquipments] = useState(0);
  const [nbUndetectedEquipments, setNbUndetectedEquipments] = useState(0);
  const [nbOptionalEquipments, setNbOptionalEquipments] = useState(0);

  const orderedEquipmentsExist = data?.orderedEquipments?.length > 0;

  // Local state to track checkbox states for immediate UI updates
  const [checkboxStates, setCheckboxStates] = useState<{
    [key: string]: {
      identifiable: boolean;
      required: boolean;
      optional: boolean;
    };
  }>({});

  const { data: equipments, isLoading: isLoadingEquipments } =
    useListAllEquipments();

  // const { mutate: updateCheckPoint, isPending: isPendingUpdateCheckPoint } =
  //   useUpdateCheckPoint({
  //     handleSuccess: (data: TSchema) => {
  //       setValue(
  //         'equipments' as Path<TSchema>,
  //         data?.orderedEquipments as PathValue<TSchema, Path<TSchema>>
  //       );
  //     },
  //   });

  /**
   * === Set Value Watchers section ===
   */
  const equipmentsValue =
    watch &&
    watch(
      'equipments' as Path<TSchema>,
      orderedEquipmentsExist ? data?.orderedEquipments : equipments?.data ?? []
    );

  const withEquipmentDetectionValue =
    watch &&
    watch(
      'withEquipmentDetection' as Path<TSchema>,
      data?.withEquipmentDetection || true
    );

  const videoDurationValue =
    watch &&
    watch('videoDuration' as Path<TSchema>, data?.videoDuration ?? null);

  const detectedEquimentTimeoutValue =
    watch &&
    watch(
      'detectedEquimentTimeout' as Path<TSchema>,
      data?.detectedEquimentTimeout ?? null
    );

  const undetectedEquimentTimeoutValue =
    watch &&
    watch(
      'undetectedEquimentTimeout' as Path<TSchema>,
      data?.undetectedEquimentTimeout ?? null
    );

  const notAutomaticValidationCheckPointTimeoutValue =
    watch &&
    watch(
      'notAutomaticValidationCheckPointTimeout' as Path<TSchema>,
      data?.notAutomaticValidationCheckPointTimeout ?? null
    );

  const percentOptionalEquipmentDetectionValue =
    watch &&
    watch(
      'percentOptionalEquipmentDetection' as Path<TSchema>,
      data?.percentOptionalEquipmentDetection ?? null
    );

  const identifiableEquipmentIdsValue =
    watch &&
    watch(
      'identifiableEquipmentIds' as Path<TSchema>,
      [] as PathValue<TSchema, Path<TSchema>>
    );

  const requiredEquipmentIdsValue =
    watch &&
    watch(
      'requiredEquipmentIds' as Path<TSchema>,
      [] as PathValue<TSchema, Path<TSchema>>
    );

  const optionalEquipmentIdsValue =
    watch &&
    watch(
      'optionalEquipmentIds' as Path<TSchema>,
      [] as PathValue<TSchema, Path<TSchema>>
    );
  /**
   * === END OF Set Value Watchers section ===
   */

  /**
   * === Reset Error Messages On First Render section (when on tab change) ===
   */
  useEffect(() => {
    setError('detectedEquimentTimeout' as Path<TSchema>, {
      message: '',
    });
    setError('undetectedEquimentTimeout' as Path<TSchema>, {
      message: '',
    });
    setError('notAutomaticValidationCheckPointTimeout' as Path<TSchema>, {
      message: '',
    });
    setError('percentOptionalEquipmentDetection' as Path<TSchema>, {
      message: '',
    });
    setError('requiredEquipmentIds' as Path<TSchema>, {
      message: '',
    });
  }, [setError]);
  /**
   * === END OF Reset Error Messages On First Render section (when on tab change) ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && (orderedEquipmentsExist || equipments?.data)) {
      setValue(
        'equipments' as Path<TSchema>,
        orderedEquipmentsExist
          ? data?.orderedEquipments
          : equipments?.data ?? []
      );
    }
  }, [setValue, orderedEquipmentsExist, data?.orderedEquipments, equipments]);

  useEffect(() => {
    if (setValue && data) {
      setCheckPointRequiredFields({
        setValue,
        data,
      });
      setValue(
        'withEquipmentDetection' as Path<TSchema>,
        data.withEquipmentDetection ?? true
      );
      setValue(
        'enablePlateCorrection' as Path<TSchema>,
        data.enablePlateCorrection ?? false
      );
      setValue(
        'enableVINCorrection' as Path<TSchema>,
        data.enableVINCorrection ?? false
      );
      setValue(
        'enableRegistrationCertificate' as Path<TSchema>,
        data.enableRegistrationCertificate ?? false
      );
    }
  }, [setValue, data]);

  useEffect(() => {
    if (setValue && data && withEquipmentDetectionValue) {
      setValue(
        'detectedEquimentTimeout' as Path<TSchema>,
        data.detectedEquimentTimeout ?? null
      );
      setValue(
        'undetectedEquimentTimeout' as Path<TSchema>,
        data.undetectedEquimentTimeout ?? null
      );
      setValue(
        'notAutomaticValidationCheckPointTimeout' as Path<TSchema>,
        data.notAutomaticValidationCheckPointTimeout ??
          NOT_AUTOMATIC_VALIDATION_CHECK_POINT_TIMEOUT_DEFAULT_VALUE
      );
      setValue(
        'percentOptionalEquipmentDetection' as Path<TSchema>,
        data.percentOptionalEquipmentDetection ?? null
      );
      setValue(
        'identifiableEquipmentIds' as Path<TSchema>,
        data.identifiableEquipmentIds ?? null
      );
      setValue(
        'requiredEquipmentIds' as Path<TSchema>,
        data.requiredEquipmentIds ?? null
      );
      setValue(
        'optionalEquipmentIds' as Path<TSchema>,
        data.optionalEquipmentIds ?? null
      );
    }
  }, [setValue, data, withEquipmentDetectionValue]);

  useEffect(() => {
    if (setValue && data && !withEquipmentDetectionValue) {
      setValue('videoDuration' as Path<TSchema>, data.videoDuration ?? null);
    }
  }, [setValue, data, withEquipmentDetectionValue]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (detectedEquimentTimeoutValue) {
      setError('detectedEquimentTimeout' as Path<TSchema>, {
        message: '',
      });
    }
  }, [detectedEquimentTimeoutValue, setError]);

  useEffect(() => {
    if (undetectedEquimentTimeoutValue) {
      setError('undetectedEquimentTimeout' as Path<TSchema>, {
        message: '',
      });
    }
  }, [undetectedEquimentTimeoutValue, setError]);

  useEffect(() => {
    if (notAutomaticValidationCheckPointTimeoutValue) {
      setError('notAutomaticValidationCheckPointTimeout' as Path<TSchema>, {
        message: '',
      });
    }
  }, [notAutomaticValidationCheckPointTimeoutValue, setError]);

  useEffect(() => {
    if (percentOptionalEquipmentDetectionValue) {
      setError('percentOptionalEquipmentDetection' as Path<TSchema>, {
        message: '',
      });
    }
  }, [percentOptionalEquipmentDetectionValue, setError]);

  useEffect(() => {
    if (requiredEquipmentIdsValue) {
      setError('requiredEquipmentIds' as Path<TSchema>, {
        message: '',
      });
    }
  }, [requiredEquipmentIdsValue, setError]);

  useEffect(() => {
    if (!requiredEquipmentIdsValue) {
      setError('requiredEquipmentIds' as Path<TSchema>, {
        message: '',
      });
    }
  }, [requiredEquipmentIdsValue, setError, activeTab]);

  useEffect(() => {
    if (videoDurationValue) {
      setError('videoDuration' as Path<TSchema>, {
        message: '',
      });
    }
  }, [videoDurationValue, setError]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Number Of Equipments section ===
   */
  useEffect(() => {
    setNbDetectedEquipments(identifiableEquipmentIdsValue?.length || 0);
    setNbUndetectedEquipments(requiredEquipmentIdsValue?.length || 0);
    setNbOptionalEquipments(optionalEquipmentIdsValue?.length || 0);
  }, [
    identifiableEquipmentIdsValue,
    requiredEquipmentIdsValue,
    optionalEquipmentIdsValue,
  ]);
  /**
   * === END OF Set Number Of Equipments section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!withEquipmentDetectionValue) {
      if (!videoDurationValue) {
        setError('videoDuration' as Path<TSchema>, {
          message: t('videoDurationRequired'),
        });
      }
    } else {
      if (!detectedEquimentTimeoutValue) {
        setError('detectedEquimentTimeout' as Path<TSchema>, {
          message: t('timeBetweenDetectedEquipmentsRequired'),
        });
      }

      if (!undetectedEquimentTimeoutValue) {
        setError('undetectedEquimentTimeout' as Path<TSchema>, {
          message: t('timeBetweenUndetectedEquipmentsRequired'),
        });
      }

      if (!percentOptionalEquipmentDetectionValue) {
        setError('percentOptionalEquipmentDetection' as Path<TSchema>, {
          message: t('percentOptionalEquipmentDetectionRequired'),
        });
      }

      if (!notAutomaticValidationCheckPointTimeoutValue) {
        setError('notAutomaticValidationCheckPointTimeout' as Path<TSchema>, {
          message: t('notAutomaticValidationCheckPointTimeoutRequired'),
        });
      }
      // else if (
      //   notAutomaticValidationCheckPointTimeoutValue <
      //   NOT_AUTOMATIC_VALIDATION_CHECK_POINT_TIMEOUT_DEFAULT_VALUE
      // ) {
      //   setError('notAutomaticValidationCheckPointTimeout' as Path<TSchema>, {
      //     message: t('thisValueShloudBeGreaterOrEqualTo60'),
      //   });
      // }

      if (!requiredEquipmentIdsValue?.length) {
        setActiveTab(EquipmentTab.REQUIRED);

        setError('requiredEquipmentIds' as Path<TSchema>, {
          message: t('requiredEquipmentsRequired'),
        });
      }
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  const onCheckEquipment = ({
    id,
    currentIds,
    currentFieldName,
    oppositeFields,
    linkFields,
  }: IOnCheckEquipment) => {
    let ids: string[];

    if (currentIds?.includes(id)) {
      // If ID is already checked, remove it from the current list
      ids = currentIds.filter((equipmentId) => equipmentId !== id);

      // Update local state immediately for UI responsiveness
      if (linkFields?.length > 0) {
        setCheckboxStates((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            required: false,
            optional: false,
          },
        }));

        // Also update the form state to keep it in sync
        setValue(
          'requiredEquipmentIds' as Path<TSchema>,
          (requiredEquipmentIdsValue || []).filter(
            (equipmentId) => equipmentId !== id
          ) as PathValue<TSchema, Path<TSchema>>
        );

        setValue(
          'optionalEquipmentIds' as Path<TSchema>,
          (optionalEquipmentIdsValue || []).filter(
            (equipmentId) => equipmentId !== id
          ) as PathValue<TSchema, Path<TSchema>>
        );
      }
    } else {
      // If not checked, add the ID to the current list
      ids = [...currentIds, id];

      // Uncheck the ID in opposite fields only when checking this field
      if (oppositeFields?.length > 0) {
        for (const field of oppositeFields) {
          if (field.ids.includes(id)) {
            const updatedOppositeIds = field.ids.filter(
              (selectedId) => selectedId !== id
            );
            setValue(
              field.name as Path<TSchema>,
              updatedOppositeIds as PathValue<TSchema, Path<TSchema>>
            );
          }
        }
      }
    }

    // Update the current field
    setValue(
      currentFieldName as Path<TSchema>,
      ids as PathValue<TSchema, Path<TSchema>>
    );

    // Update local state for immediate UI response
    setCheckboxStates((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [currentFieldName === 'identifiableEquipmentIds'
          ? 'identifiable'
          : currentFieldName === 'requiredEquipmentIds'
          ? 'required'
          : 'optional']: !currentIds.includes(id),
      },
    }));
  };

  /**
   * === Handle Drag And Drop section ===
   */

  const {
    rowData,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useRowDragAnDrop({
    data: equipmentsValue,
    setValue,
    dragIdentifierField: 'id',
    fieldName: data?.orderedEquipments ? 'orderedEquipments' : 'equipments',
    // onReorder: (reorderedItems) => {
    //   const newEquipmentList = [];

    //   reorderedItems.forEach((item) => {
    //     newEquipmentList.push({
    //       id: item?.id,
    //       index: item?.index,
    //     });

    //     updateCheckPoint({
    //       ...data,
    //       orderedEquipments: newEquipmentList,
    //     });
    //   });
    // },
  });

  /**
   * === END OF HandleDrag And Drop section ===
   */

  const renderEquipments = () => {
    if (!isLoadingEquipments && equipmentsValue?.length > 0) {
      return getSortedList(
        rowData as TEquipmentSchema[] | TSchema[],
        'index',
        'asc'
      )?.map((equipment) => {
        let completeEquipment = equipment;

        if (orderedEquipmentsExist) {
          completeEquipment = equipments?.data?.find(
            (dbEquipment) => dbEquipment?.id === equipment?.id
          );
        }

        const { id, name } = completeEquipment;

        const translatedEquipment = getTranslatedItem(
          completeEquipment?.['translatedEquipments']
        );

        const checkedOptId =
          checkboxStates[id]?.optional !== undefined
            ? checkboxStates[id].optional
            : optionalEquipmentIdsValue?.find((checkedId) => checkedId === id);

        const checkedRequiredId =
          checkboxStates[id]?.required !== undefined
            ? checkboxStates[id].required
            : requiredEquipmentIdsValue?.find((checkedId) => checkedId === id);

        const checkedIdentifiableId =
          checkboxStates[id]?.identifiable !== undefined
            ? checkboxStates[id].identifiable
            : identifiableEquipmentIdsValue?.find(
                (checkedId) => checkedId === id
              );

        return (
          <Accordion
            key={equipment?.id}
            triggerWrapperClassName="flex gap-1 px-4 py-2.5 bg-slate-100 border border-slate-200 rounded"
            titleClassName="flex gap-3"
            title={
              <>
                <Checkbox
                  checkboxClassName="!border-purple-5433FF/70"
                  name={`${equipment?.id}-identifiable`}
                  checked={checkedIdentifiableId}
                  onChange={() =>
                    onCheckEquipment({
                      id,
                      currentIds: identifiableEquipmentIdsValue,
                      currentFieldName: 'identifiableEquipmentIds',
                      oppositeFields: [],
                      linkFields: [
                        {
                          ids: requiredEquipmentIdsValue,
                          name: 'requiredEquipmentIds',
                        },
                        {
                          ids: optionalEquipmentIdsValue,
                          name: 'optionalEquipmentIds',
                        },
                      ],
                    })
                  }
                />
                <Checkbox
                  checkboxClassName={twMerge(
                    '',
                    !(
                      !checkedRequiredId &&
                      !checkedIdentifiableId &&
                      !checkedOptId
                    ) && '!border-red-FF0A0A/50 checked:!bg-red-FF0A0A'
                  )}
                  name={`${equipment?.id}-required`}
                  checked={checkedRequiredId}
                  disabled={
                    !checkedRequiredId &&
                    !checkedIdentifiableId &&
                    !checkedOptId
                  }
                  onChange={() =>
                    onCheckEquipment({
                      id,
                      currentIds: requiredEquipmentIdsValue,
                      currentFieldName: 'requiredEquipmentIds',
                      oppositeFields: [],
                      linkFields: [],
                    })
                  }
                />
                <Checkbox
                  checkboxClassName={twMerge(
                    '',
                    !(
                      !checkedOptId &&
                      !checkedIdentifiableId &&
                      !checkedRequiredId
                    ) &&
                      '!border-purple-A713F6/45 checked:!border-purple-DF00FF/30 checked:!bg-purple-DF00FF/65'
                  )}
                  name={`${equipment?.id}-optional`}
                  checked={checkedOptId}
                  disabled={
                    !checkedOptId &&
                    !checkedIdentifiableId &&
                    !checkedRequiredId
                  }
                  onChange={() =>
                    onCheckEquipment({
                      id,
                      currentIds: optionalEquipmentIdsValue,
                      currentFieldName: 'optionalEquipmentIds',
                      oppositeFields: [],
                      linkFields: [],
                    })
                  }
                />
                <div className="font-normal text-[13.5px] text-gray-700">
                  {translatedEquipment?.name || name}
                </div>
              </>
            }
            draggable
            onDragStart={(event) => handleDragStart(event, equipment?.id)}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(event) => handleDrop(event, equipment?.id)}
          />
        );
      });
    }

    return undefined;
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('common:setting')}
              defaultIconClassName="ki-setting-2"
            />
          }
          footer={
            <Button
              buttonClassName="px-8"
              text={t('common:save')}
              disabled={submitDisabled}
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <>
            <FormSectionTitle
              title={t('configureCheckPointDetectionLogic')}
              icon={
                <i className="h-0 ki-outline ki-abstract-45 !text-[25px] -mt-0.75" />
              }
            />
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Controller
                name="withEquipmentDetection"
                control={control}
                render={({ field }) => (
                  <Switch
                    errorMessage={
                      errors &&
                      (errors.withEquipmentDetection?.message as string)
                    }
                    checked={field.value}
                    onChange={(checked) => {
                      field.onChange(checked);
                    }}
                    leftTextClassName="text-slate-600 font-medium mr-1"
                    leftText={t('withEquipmentDetection')}
                  />
                )}
              />
            </div>
            {withEquipmentDetectionValue ? (
              <>
                <hr className="my-4" />
                <div className="flex flex-col gap-8">
                  <FormField
                    text={t('detectedMinimumRequiredEquipmentTimeout')}
                    subText={t(
                      'detectedMinimumRequiredEquipmentTimeoutSubText'
                    )}
                    textOuterWrapperClassName="w-[650px]"
                    textClassName="text-slate-700 font-normal"
                    subTextClassName="text-gray-600"
                    required
                  >
                    <div className="flex flex-1 flex-wrap items-center gap-3">
                      <Input
                        {...(register &&
                          register('detectedEquimentTimeout' as Path<TSchema>))}
                        errorMessage={
                          errors &&
                          (errors.detectedEquimentTimeout?.message as string)
                        }
                        placeholder={t('waitingTimeBetweenDetectedEquipments')}
                        onInput={(e) => {
                          // Enforce to input only a positive number
                          e.currentTarget.value =
                            Number(e.currentTarget.value) > -1
                              ? e.currentTarget.value
                              : null;
                        }}
                        type="number"
                      />
                      <div className="text-sm text-gray-5B5B5B font-semibold">
                        {t('common:seconds')}
                      </div>
                    </div>
                  </FormField>
                  <FormField
                    text={t('undetectedMinimumRequiredEquipmentTimeout')}
                    subText={t(
                      'undetectedMinimumRequiredEquipmentTimeoutSubText'
                    )}
                    textOuterWrapperClassName="w-[650px]"
                    textClassName="text-slate-700 font-normal"
                    subTextClassName="text-gray-600"
                    required
                  >
                    <div className="flex flex-1 flex-wrap items-center gap-3">
                      <Input
                        {...(register &&
                          register(
                            'undetectedEquimentTimeout' as Path<TSchema>
                          ))}
                        errorMessage={
                          errors &&
                          (errors.undetectedEquimentTimeout?.message as string)
                        }
                        placeholder={t(
                          'waitingTimeBetweenUndetectedEquipments'
                        )}
                        onInput={(e) => {
                          // Enforce to input only a positive number
                          e.currentTarget.value =
                            Number(e.currentTarget.value) > -1
                              ? e.currentTarget.value
                              : null;
                        }}
                        type="number"
                      />
                      <div className="text-sm text-gray-5B5B5B font-semibold">
                        {t('common:seconds')}
                      </div>
                    </div>
                  </FormField>
                  <FormField
                    text={t('notAutomaticValidationCheckPointTimeout')}
                    subText={t(
                      'notAutomaticValidationCheckPointTimeoutSubText'
                    )}
                    textOuterWrapperClassName="w-[650px]"
                    textClassName="text-slate-700 font-normal"
                    subTextClassName="text-gray-600"
                    required
                  >
                    <div className="flex flex-1 flex-wrap items-center gap-3">
                      <Input
                        {...(register &&
                          register(
                            'notAutomaticValidationCheckPointTimeout' as Path<TSchema>
                          ))}
                        errorMessage={
                          errors &&
                          (errors.notAutomaticValidationCheckPointTimeout
                            ?.message as string)
                        }
                        placeholder={t(
                          'notAutomaticValidationCheckPointTimeoutSubText'
                        )}
                        onInput={(e) => {
                          // Enforce to input only a positive number
                          e.currentTarget.value =
                            Number(e.currentTarget.value) > -1
                              ? e.currentTarget.value
                              : null;
                        }}
                        type="number"
                      />
                      <div className="text-sm text-gray-5B5B5B font-semibold">
                        {t('common:seconds')}
                      </div>
                    </div>
                  </FormField>
                  <hr className="my-4" />
                  <FormField
                    text={t('percentageOfOptionalDetectedEquipment')}
                    textClassName="text-slate-700 font-normal"
                    textOuterWrapperClassName="w-[650px]"
                    required
                  >
                    <div className="flex flex-1 flex-wrap items-center gap-3">
                      <Input
                        {...(register &&
                          register(
                            'percentOptionalEquipmentDetection' as Path<TSchema>
                          ))}
                        errorMessage={
                          errors &&
                          (errors.percentOptionalEquipmentDetection
                            ?.message as string)
                        }
                        placeholder={t('detectionPercentage')}
                        onInput={(e) => {
                          // Enforce to input only a positive number
                          e.currentTarget.value =
                            Number(e.currentTarget.value) > -1
                              ? e.currentTarget.value
                              : null;
                        }}
                        type="number"
                      />
                      <div className="text-sm text-gray-5B5B5B font-extrabold w-[65px]">
                        %
                      </div>
                    </div>
                  </FormField>
                </div>
              </>
            ) : (
              <>
                <hr className="my-4" />
                <FormField
                  text={t('videoDuration')}
                  subText={t(
                    'videoDurationBeforeAutomaticSwitchToNextCheckPoint'
                  )}
                  textOuterWrapperClassName="w-[650px]"
                  textClassName="text-slate-700 font-normal"
                  subTextClassName="text-gray-600"
                  required
                >
                  <div className="flex flex-1 flex-wrap items-center gap-3">
                    <Input
                      {...(register &&
                        register('videoDuration' as Path<TSchema>))}
                      errorMessage={
                        errors && (errors.videoDuration?.message as string)
                      }
                      placeholder={t('videoDuration')}
                      onInput={(e) => {
                        // Enforce to input only a positive number
                        e.currentTarget.value =
                          Number(e.currentTarget.value) > -1
                            ? e.currentTarget.value
                            : null;
                      }}
                      type="number"
                    />
                    <div className="text-sm text-gray-5B5B5B font-semibold">
                      {t('common:seconds')}
                    </div>
                  </div>
                </FormField>
              </>
            )}
            {withEquipmentDetectionValue && (
              <>
                <hr className="my-4" />

                {errors?.requiredEquipmentIds?.message && (
                  <div>
                    <ErrorMessage
                      className="text-md"
                      message={
                        <>
                          <span className="text-xl">*</span>{' '}
                          <span>
                            {errors?.requiredEquipmentIds?.message as string}
                          </span>
                        </>
                      }
                    />
                  </div>
                )}

                <div className="ml-4 flex gap-10">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="-mt-0.75 ki-outline ki-fingerprint-scanning text-primary text-lg" />
                    <div className="flex gap-1.5">
                      <div className="text-md font-medium">
                        {t('identifiableEquipments')}
                      </div>
                      <div className="text-md font-medium">
                        ({nbDetectedEquipments})
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <i className="-mt-[4px] ki-outline ki-check-circle text-red-FF2929 text-lg" />
                    <div className="flex gap-1.5">
                      <div className="text-md font-medium">
                        {t('requiredEquipments')}
                      </div>
                      <div className="text-md font-medium">
                        ({nbUndetectedEquipments})
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <i className="-mt-1 ki-outline ki-simcard-2 text-purple-DF00FF/65 text-lg" />
                    <div className="flex gap-1.5">
                      <div className="text-md font-medium">
                        {t('optionalEquipments')}
                      </div>
                      <div className="text-md font-medium">
                        ({nbOptionalEquipments})
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex gap-x-[17px]">
                      <i className="ml-4 ki-outline ki-fingerprint-scanning self-center text-primary text-lg" />
                      <i className="-ml-0.5 ki-outline ki-check-circle self-center text-red-FF2929 text-lg" />
                      <i className="-ml-0.5 ki-outline ki-simcard-2 self-center text-purple-DF00FF/65 text-lg" />
                    </div>
                  ))}
                  {renderEquipments()}
                </div>
              </>
            )}
          </>
        </Card>
      </form>
      {/* {(isLoadingEquipments || isPendingUpdateCheckPoint) && <Loader />} */}
      {isLoadingEquipments && <Loader />}
    </>
  );
};
